/**
 * Turning a scene back into pixels, away from the screen it was drawn on.
 *
 * Every picture in the app is rendered through here: the thumbnail on the
 * shelf, and the full-size image in an export. It deliberately does NOT
 * screenshot the visible canvas — on an infinite pad the viewport is a window
 * onto the drawing, not the drawing, and saving the window is how you lose the
 * half a child scrolled past.
 */

import type { Scene } from '../lib/scene.ts';
import { sceneBounds } from '../lib/scene.ts';
import { drawStroke } from './render.ts';
import { mirrorRadius, paintMirrored } from './mirror.ts';

export interface Snapshot {
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
}

/** The world rectangle a scene occupies, whichever kind of scene it is. */
function frameOf(scene: Scene): { x: number; y: number; w: number; h: number } | null {
  if (scene.kind === 'mirror') {
    const r = mirrorRadius(scene.strokes);
    if (r <= 0) return null;
    const pad = r * 1.06;
    return { x: -pad, y: -pad, w: pad * 2, h: pad * 2 };
  }
  const box = sceneBounds(scene);
  if (!box) return null;
  return { x: box.minX, y: box.minY, w: box.maxX - box.minX, h: box.maxY - box.minY };
}

/**
 * Render the whole scene onto its own canvas, longest side `maxSize`.
 * Returns null for an empty scene — there is no picture to make.
 */
export function renderScene(scene: Scene, maxSize: number, background: string): Snapshot | null {
  if (typeof document === 'undefined') return null;
  const frame = frameOf(scene);
  if (!frame) return null;

  const scale = Math.min(maxSize / frame.w, maxSize / frame.h, 3);
  const width = Math.max(1, Math.round(frame.w * scale));
  const height = Math.max(1, Math.round(frame.h * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.fillStyle = background;
  ctx.fillRect(0, 0, width, height);

  ctx.save();
  ctx.scale(scale, scale);
  ctx.translate(-frame.x, -frame.y);
  if (scene.kind === 'mirror') {
    for (const s of scene.strokes) paintMirrored(ctx, s, scene.folds ?? 6, scene.flip ?? true);
  } else {
    for (const s of scene.strokes) drawStroke(ctx, s);
  }
  ctx.restore();

  return { canvas, width, height };
}
